import { skipToken } from '@tanstack/react-query';
import { useState } from 'react';
import { sortByDesc } from '../../../shared/utils/sortUtils.ts';
import { Batch, Operation } from '../../types/sharedTypeImpl.ts';
import { getOperationsWithAlarms } from '../../utils/toolChangeAlarms.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { ClearFiltersButton } from '../clearFiltersButton.tsx';
import { OperationDialog } from '../operation-dialog/operationDialog.tsx';
import { getOperationColor } from './operationColors.ts';
import { OperationsTable } from './operationsTable.tsx';

interface OperationDialogState {
    operationUnderEdit: Operation | null;
}

interface OperationTypeButton {
    name: string;
    opType: string;
    enabled: boolean;
}


interface Props {
    batch: Batch | null;
    onOperationSaved: (operation: Operation, newScrapLabel: number | undefined) => void;
}

export function FilterableOperationTable({
    batch,
    onOperationSaved,
}: Props) {

    const [searchQuery, setSearchQuery] = useState('');
    const [operationDialogState, setOperationDialogState] = useState<OperationDialogState | null>(null);

    const [operationTypeButtons, setOperationTypeButtons] = useState<OperationTypeButton[]>([
        { name: 'Adjustments', opType: 'Adjustments', enabled: true },
        { name: 'Offset', opType: 'Offset', enabled: true },
        { name: 'Tool change', opType: 'Tool change', enabled: true },
        { name: 'Program edit', opType: 'Program edit', enabled: true },
        { name: 'Fix tool change', opType: 'Fix unscheduled tool change', enabled: true },
        { name: 'Scrap', opType: 'Scrap', enabled: true },
        { name: 'Measurement', opType: 'Measurement', enabled: false },
    ]);

    const { data: operations, isLoading, refetch } = trpc.operations.get.useQuery(
        batch ? {
            equipment: batch.equipment,
            product: batch.product,
            effectiveTime: { start: batch.start, end: batch.end },
        } : skipToken, {
        select: (operations) => sortByDesc(getOperationsWithAlarms(operations), it => it.effectiveTime),
        refetchInterval: 5 * 60_000,
    });

    const filteredOperations = operations?.filter(operation =>
        operationTypeButtons.find(button => button.opType === operation.operationType)?.enabled
    )?.filter(operation => {
        const query = searchQuery.trim().toLowerCase();
        if (query === '') return true;
        return operation.comment?.toLowerCase().includes(query)
            || operation.operator?.toLowerCase().includes(query)
            || operation.details?.some(detail => detail?.toLowerCase().includes(query))
            || operation.collateralDamage?.some(tool => tool.toolNumber.toLowerCase().includes(query))
            || operation.collateralDamage?.some(tool => tool.toolVariant.toLowerCase().includes(query));
    });

    const handleClearFilters = () => {
        setSearchQuery('');
    }

    const onOperationTypeClick = (operationType: string) => {
        const updatedButtons = operationTypeButtons.map(it => it.name === operationType ? { ...it, enabled: !it.enabled } : it);
        setOperationTypeButtons(updatedButtons);
    }

    const getToggleButtonStyle = (button: OperationTypeButton) => {
        const color = getOperationColor(button.opType);
        return button.enabled
            ? { backgroundColor: color, color: '#fff' }
            : { borderColor: color };
    }


    if (batch == null) return null;
    if (isLoading) return <p>Loading...</p>;
    return (
        <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                    className='button--large'
                    disabled={!batch}
                    onClick={() => setOperationDialogState({ operationUnderEdit: null })}
                >
                    Add operation
                </button>

                <div style={{ flexGrow: 1 }} />

                <input
                    id='operationFilterSearch'
                    type='text'
                    placeholder='Search'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ClearFiltersButton
                    style={{ marginRight: '16px' }}
                    onClick={() => handleClearFilters()}
                />

                {operationTypeButtons.map(button => (
                    <button
                        key={button.name}
                        className={'toggle-button'}
                        style={{ ...getToggleButtonStyle(button) }}
                        onClick={() => onOperationTypeClick(button.name)}
                    >
                        {button.name}
                    </button>
                ))}
            </div>

            {batch && <OperationsTable
                narrow={true}
                operations={filteredOperations ?? []}
                showBatch={false}
                operationTypeIndicators={true}
                highlightPotentialScraps={true}
                showScrapLabels={false}
                showScrapReasons={false}
                showOrderNumbers={false}
                enableScrapReview={false}
                enableScrapClose={false}
                showScrapCloseValues={false}
                showEditColumn={true}
                hideEditIfReviewedOrClosed={true}
                enableRevert={false}
                onEditClick={(operation) => setOperationDialogState({ operationUnderEdit: operation })}
            />}

            <OperationDialog
                isOpen={!!operationDialogState}
                batch={batch}
                otherOperations={operations}
                operationUnderEdit={operationDialogState?.operationUnderEdit}
                onClose={() => setOperationDialogState(null)}
                onOperationSaved={(operation, newScrapLabel) => { refetch(); onOperationSaved(operation, newScrapLabel) }}
            />
        </div >
    );
}
